<template>
  <section>
    <PageTitle>
      <span v-html="groupName" />
    </PageTitle>
    <PageContent v-if="coordinateList.length === 0">
      <Container>
        <NoticeBox
          type="error"
          :borderless="true"
          class="space-b-2 text-bold"
        >
          現在、コーディネートがありません
        </NoticeBox>
        <p class="text-paragraph text-xs">
          現在表示されるコーディネートがありません。
          <br class="mobile-only" />
          コーディネートがある場合は、この画面に表示されます。
        </p>
      </Container>
    </PageContent>
    <PageContent v-else>
      <Container>
        <NoticeBox
          v-if="isUsageLimitOver"
          type="info"
          :icon-class="['fas', 'fa-lock']"
        >
          利用制限日を過ぎたため、変更できません
        </NoticeBox>
        <ul class="coordinate-list">
          <li
            class="coordinate"
            v-for="coordinate in filterCoordinateList"
            :key="coordinate.coordinate_id"
          >
            <div
              class="coordinate-link"
              :class="{ 'disabled': isUsageLimitOver }"
              @click="modalOpen(coordinate)"
            >
              <figure>
                <div class="coordinate-image-box">
                  <img v-if="coordinate.image_path"
                    class="coordinate-image"
                    :src="coordinate.image_path | appendDomain"
                    :alt="coordinate.coordinate_name"
                  />
                </div>
                <figcaption>
                  <p
                    class="coordinate-name"
                    v-html="coordinate.coordinate_name"
                  ></p>
                </figcaption>
              </figure>
            </div>
          </li>
        </ul>
      </Container>
    </PageContent>
    <Modal
      id="modal-coordinate"
      class="is-scrollable"
      title="このコーディネートを選択しますか？"
    >
      <template>
        <p class="text-paragraph text-xs">
          ※既に別のコーディネート商品をお気に入りや見積シミュレーションに登録している場合はリセットされます。
        </p>
      </template>
      <template #footer>
        <WaveButton
          type="button"
          size="small"
          :text="true"
          :gray="true"
          @click="$modal.close('modal-coordinate')"
        >いいえ</WaveButton>
        <WaveButton
          type="button"
          size="small"
          :text="true"
          :danger="true"
          @click="modalOk"
        >はい</WaveButton>
      </template>
    </Modal>
    <Modal
      id="modal-coordinate-usage-limit-over"
      class="is-scrollable"
      title="利用制限日を過ぎています。"
    >
      <template #footer>
        <WaveButton
          type="button"
          size="small"
          :text="true"
          :gray="true"
          @click="$modal.close('modal-coordinate-usage-limit-over')"
        >閉じる</WaveButton>
      </template>
    </Modal>
  </section>
</template>

<script>
import { renderError } from '@/utils/error'
import { SCHEME, getDomain } from '@/api/theme'
import bridalItemApi from '@/api/bridal-item'
import Modal from '@/components/Modal'
import NoticeBox from '@/components/NoticeBox.vue'


const storagePath = SCHEME + getDomain()
const RESPONSE_USAGE_LIMIT_OVER = 490

export default {
  metaInfo() {
    return {
      title: this.$store.getters['app/getMenuName']('bridal-item'),
      bodyAttrs: { class: 'bridal-item-coordinate' }
    }
  },
  data() {
    return {
      groupName: "",
      revision: 1,
      coordinateList: [],
      coordinateId: undefined,
      updateParams: {
        bridal_item_group_id: undefined,
        coordinate_id: undefined,
        target: undefined,
        revision: undefined,
      },
      updateResponse: undefined,
      loading: false,
    }
  },
  components: {
    Modal,
    NoticeBox,
  },
  computed: {
    businessId() {
      return this.$store.getters['auth/businessId']
    },
    groupId() {
      return Number(this.$route.params.groupId)
    },
    selectedCoordinateId() {
      for (const row of this.coordinateList) {
        if (row.target === true) {
          return row.coordinate_id
        }
      }
      return null
    },
    filterCoordinateList() {
      // 新郎新婦の場合customer_non_display_flagがfalseのみ表示する
      if (this.$store.getters['auth/isCustomer']) {
        const result = this.coordinateList.filter((coordinate) => {
          return (coordinate.customer_non_display_flag === false);
        });
        return result
      }
      return this.coordinateList
    },
    isUsageLimitOver() {
      if (
        this.$store.getters['auth/isCustomer']
        && this.$store.getters['app/getUsageLimitFlag']
        && this.$store.getters['app/getUsageLimitDays'] === 0
      ) {
        return true;
      } else {
        return false;
      }
    },
  },
  methods: {
    async fetchCoordinate() {
      try {
        const result = await bridalItemApi.getCoordinate(this.businessId, this.groupId)
        this.revision = result.revision
        this.groupName = result.bridal_item_group_name
        this.coordinateList = result.coordinate_list
      } catch (err) {
        renderError(err)
      }
    },
    modalOpen(val) {
      if (this.isUsageLimitOver === true) {
        return
      }
      try {
        this.coordinateId = val.coordinate_id
        // Modal Open
        this.$modal.show('modal-coordinate')
      } catch (err) {
        renderError(err)
      }
    },
    async modalOk() {
      if (this.loading) {
        return
      }
      this.loading = true
      try {
        if (this.isUsageLimitOver === true) {
          this.$modal.close('modal-coordinate');
          this.$modal.show('modal-coordinate-usage-limit-over');
        } else {
          if (this.selectedCoordinateId !== null) {
              // 解除
              this.updateParams = {
                bridal_item_group_id: this.groupId,
                coordinate_id: this.selectedCoordinateId,
                target: false,
                revision: this.revision,
              }
              this.updateResponse = await bridalItemApi.updateCoordinate(
                this.businessId,
                this.updateParams
              )
              this.revision = this.updateResponse.revision
          }
          // 登録
          this.updateParams = {
            bridal_item_group_id: this.groupId,
            coordinate_id: this.coordinateId,
            target: true,
            revision: this.revision,
          }
          await bridalItemApi.updateCoordinate(this.businessId, this.updateParams)
          this.$wcRouter.push({
            name: 'bridal-item.show',
            params: this.groupId
          })
        }
      } catch (err) {
        this.$modal.close('modal-coordinate')
        this.loading = false
        renderError(err, {onErrorPopupClose: () => {
          // 利用制限エラーの場合、画面リロード
          if (err.response.status === RESPONSE_USAGE_LIMIT_OVER) {
            this.$router.go({
              path: this.$router.currentRoute.path,
              force: true,
            })
          }
        }})
      }
    }
  },
  filters: {
    appendDomain(val) {
      return storagePath + val
    }
  },
  created() {
    this.$withLoading(this.fetchCoordinate)
  },
}
</script>